import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import {
    get as _get,
    cloneDeep as _cloneDeep,
    sortBy as _sortBy,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import IssuesIcon from '@mui/icons-material/ErrorOutline';

import CMCTable from '../../components/cmcs/cmctable';
import CMCSidebar from '../../components/cmcs/cmcsidebar';

import CMCsService from '../../data/cmcs-service';
import PolicyService from '../../data/policy-service';
import SensorsService from '../../data/sensors-service';
import Auth, { ProfileContext } from '../../data/auth-service';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    paperBackground: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    allSensorBackground: {
        backgroundColor: theme.palette.background.paper
    },
    closedAccordion: {
        display: 'none',
        height: '0px',
        minHeight: '0px',
    },
    menuItem: {
        minWidth: '150px',
    },
    normalOutlined: {
        borderColor: theme.palette.success.main,
        color: theme.palette.success.main,
        '&:hover, &:focus, &:active': {
            borderColor: theme.palette.success.dark,
            color: theme.palette.success.dark,
        }
    },
    normalContained: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.success.dark,
        }
    },
    warningOutlined: {
        borderColor: theme.palette.warning.main,
        color: theme.palette.warning.main,
        '&:hover, &:focus, &:active': {
            borderColor: theme.palette.warning.dark,
            color: theme.palette.warning.dark,
        }
    },
    warningContained: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.warning.dark,
        }
    },
    criticalOutlined: {
        borderColor: theme.palette.error.main,
        color: theme.palette.error.main,
        '&:hover, &:focus, &:active': {
            borderColor: theme.palette.error.dark,
            color: theme.palette.error.dark,
        }
    },
    criticalContained: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.error.dark,
        }
    },
    policyFilterListItem: {
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: '12px',
        color: theme.palette.primary.main,
        height: '32px',
        padding: '5px',
        cursor: 'pointer',
        marginRight: '10px',
        marginBottom: '8px',
    },
    policyFilterCritical: {
        border: `1px solid ${theme.palette.error.main}`,
        color: theme.palette.error.main,
    },
    policyFilterWarning: {
        border: `1px solid ${theme.palette.warning.main}`,
        color: theme.palette.warning.main,
        background: theme.palette.mode === 'light' ? '#000' : 'inherit',
    },
    policyFilterNormal: {
        border: `1px solid ${theme.palette.success.main}`,
        color: theme.palette.success.main,
    },
    policyFilterCloseIcon: {
        width: '18px',
        marginTop: '-1px',
        marginLeft: '2px',
    },
    status: {
        height: '20px',
        width: '20px',
        borderRadius: '20px',
    },
    normal: {
        background: theme.palette.success.main,
    },
    warning: {
        background: theme.palette.warning.main,
    },
    critical: {
        background: theme.palette.error.main,
    },
}));

const SORT_TYPES = [
    { value: 'status', name: 'Status' },
    { value: 'name', name: 'Name' },
    { value: 'host', name: 'Host' },
];

const CMCs = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const theme = useTheme();
    const [showIssues, setShowIssues] = useState(true);
    const [filter, setFilter] = useState({
        type: 'All',
        q: '',
        limit: 1000,
        name: '',
        host: '',
        model: '',
        status: {
            critical: true,
            warning: true,
            normal: true,
        },
        policies: [],
        sort: 'status+desc'
    });
    // const [filterType, setFilterType] = useState('search');
    const [cmcs, setCMCs] = useState([]);
    const [filteredCMCs, setFilteredCMCs] = useState([]);
    // const [init, setInit] = useState(true);
    const [sortAnchor, setSortAnchor] = useState(null);
    const [policies, setPolicies] = useState([]);
    const [allJobs, setAllJobs] = useState([]);
    const [, setKick] = useState();
    const [readonly] = useState(!Auth.permissions.get('CMC').write);
    const { updateProfile, getProfile } = useContext(ProfileContext);
    // const [columnWidth, setColumnWidth] = useState({});
    const [expandAll, setExpandAll] = useState(false);

    useEffect(() => {
        const p = getProfile('cmcs');
        setShowIssues(typeof p.showIssues === 'boolean' ? p.showIssues : true);
        setExpandAll(typeof p.expandAll === 'boolean' ? p.expandAll : false);

        getPolicies();
        getCMCs('');
        const getCMCInterval = setInterval(() => {
            getCMCs('');
        }, 15000);

        return () => {
            clearInterval(getCMCInterval);
        };
    }, []);

    useEffect(() => {
        runFilter(cmcs);
    }, [filter, cmcs]);

    const getPolicies = () => {
        PolicyService.getPoliciesWithTypes().then(res => {
            setPolicies(res);
        }, err => {
            console.error('Unable to get policies ::: ', err);
        });
    };

    const runFilter = (c) => {
        // If type is All or CMCs then apply the filter here.
        const s = filter.sort.split('+');

        if (filter.type !== 'CMCs') {
            c = _cloneDeep(c).map(v => {
                if (v.sensors) {
                    v.sensors = applyFilter(v.sensors);
                    v.sensors = _sortBy(v.sensors, s[0]);
                    if (s[1] === 'asc') {
                        v.sensors.reverse();
                    }
                }
                return v;
            });
        }
        if (filter.type !== 'Sensors') {
            c = applyFilter(c);
            c = _sortBy(c, s[0]);
            if (s[1] === 'asc') {
                c.reverse();
            }
        }

        setFilteredCMCs(c);
    };

    const applyFilter = (data) => {
        return data.filter(v => {
            if (filter.type === 'All' && (v.sensors && v.sensors.length)) return v;
            if (filter.q === '') return v;
            if (
                _get(v, 'name', '').includes(filter.q) ||
                _get(v, 'data.name', '').includes(filter.q) ||
                _get(v, 'host', '').includes(filter.q) ||
                _get(v, 'model', '').includes(filter.q)
            ) {
                return v;
            }
        }).filter(v => {
            if (
                (filter.status.critical && filter.status.warning && filter.status.normal) ||
                (!filter.status.critical && !filter.status.warning && !filter.status.normal) ||
                (filter.status.critical && v.status === 'critical') ||
                (filter.status.warning && v.status === 'warning') ||
                (filter.status.normal && v.status === 'normal')
            ) {
                return v;
            }
        }).filter(v => {
            if (filter.policies.length) {
                if (v.hasOwnProperty('cmc')) {
                    let r = true;
                    if (v.deliveries) {
                        for (let i = filter.policies.length;i--;) {
                            if (v.deliveries.hasOwnProperty(filter.policies[i].type)) {
                                r = v.deliveries[filter.policies[i].type].name === filter.policies[i].name;
                                if (!r) break;
                            }
                        }
                    }
                    return r;
                } else {
                    // If it's a CMC
                    if (v.data && v.data.jobs) {
                        for (let i = filter.policies.length;i--;) {
                            if (v.data.jobs.hasOwnProperty(filter.policies[i].type)) {
                                return v.data.jobs[filter.policies[i].type].includes(filter.policies[i].name);
                            }
                        }
                    }
                }
            } else {
                return v;
            }
        });
    };

    const getCMCs = (search) => {
        // display list first, ask question later.
        CMCsService.list().then(res => {
            // mapify
            const m = (res || []).reduce((a, c) => {
                return { ...a, [c.uuid]: c };
            }, {});

            setCMCs(current => {
                (current || []).forEach((c, i) => {
                    if (!m[c.uuid]) {
                        current.splice(i, 1);
                    } else {
                        // don't lose sensors - those will be reconciled later
                        current[i] = {
                            ...m[c.uuid],
                            sensors: current[i].sensors,
                        };
                    }
                });
                // add new CMCs
                Object.entries(m).forEach(([k, v]) => {
                    const i = (current || []).findIndex(s => s.uuid === k);
                    if (i < 0) {
                        current.push(v);
                    }
                });
                current.filter(c => c.standby !== 'Standby')
                    .forEach(c => {
                        c.loading = true;
                        setCmcSensors(c);
                    });
                return [...current];
            });
            setKick(new Date());
        }, err => {
            console.error(err);
        });
    };

    // get sensors one at a time so we don't get stuck on an unresponsive cmc
    const setCmcSensors = useCallback((cmc) => {
        SensorsService.getData('', cmc ? cmc.name : undefined).then(r => {
            const jobsMap = new Map();
            if (r) {
                r.forEach(sensor => {
                    if (sensor.data?.jobs) {
                        Object.entries(sensor.data.jobs).forEach(([type, jobInfo]) => {
                            if (jobInfo?.name) {
                                // Use composite key to deduplicate jobs
                                const key = `${type}|${jobInfo.name}`;
                                if (!jobsMap.has(key)) {
                                    jobsMap.set(key, {
                                        name: jobInfo.name,
                                        pending: false,
                                        source: "cmc",
                                        type: '',
                                        jobType: type,
                                        cmcName: cmc.name,
                                        cmcUuid: cmc.uuid
                                    });
                                }
                            }
                        });
                    }
                });
            }
            
            // Convert Map to array of unique jobs
            const jobsData = Array.from(jobsMap.values());

            setAllJobs(currentJobs => {
                const otherCmcJobs = currentJobs.filter(j => j.cmcUuid !== cmc.uuid);
                return [...otherCmcJobs, ...jobsData];
            });
            setCMCs(current => {
                const i = (current || []).findIndex(s => s.uuid === cmc.uuid);
                if (i < 0) {
                    return current;
                }

                if (!r) {
                    // all sensors are gone, remove
                    current[i].sensors = [];
                    return [...current];
                }
                // mapify
                const m = r.reduce((a, c) => {
                    return { ...a, [c.uuid]: c };
                }, {});
                const c = current[i];
                // remove sensors
                (c.sensors || []).forEach((s, i) => {
                    if (!m[s.uuid]) {
                        c.sensors.splice(i, 1);
                    }
                });
                // upsert sensors without reordering
                r.forEach(rs => {
                    const i = (c.sensors || []).findIndex(s => s.uuid === rs.uuid);
                    if (i >= 0) {
                        c.sensors[i] = rs;
                    } else {
                        c.sensors = c.sensors || [];
                        c.sensors.push(rs);
                    }
                });
                c.loading = false;
                current[i] = c;
                return [...current];
            });
        }, e => {
            console.error(e);
        });
    }, []);

    const handleAddCMC = () => {
        navigate('/cmcs/add');
    };

    const handleUpdateFilter = (f) => {
        setFilter(f);
    };

    const handleUpdateSort = (s) => {
        let d = 'desc';

        if (filter.sort.split('+')[0] === s) {
            d = (filter.sort.split('+')[1] === 'desc' ? 'asc' : 'desc');
        }

        setFilter({
            ...filter,
            sort: `${s}+${d}`,
        });
    };

    const handleCloseSort = () => {
        setSortAnchor(null);
    };

    const handleToggleIssues = () => {
        setShowIssues(!showIssues);
        updateProfile('cmcs', {
            ...getProfile('cmcs'),
            showIssues: !showIssues,
        });
    };

    const handleExpandAll = () => {
        setExpandAll(!expandAll);
        updateProfile('cmcs', {
            ...getProfile('cmcs'),
            expandAll: !expandAll,
        });
    };

    const handleClearFilter = (field) => {
        const f = { ...filter };
        f[field] = '';
        setFilter(f);
    };

    const handleClearStatus = (status) => {
        const f = { ...filter };
        f.status[status] = false;
        setFilter(f);
    };

    const handlePolicySelect = (type, name) => {
        const f = { ...filter };
        const typeAndPolicyExists = f.policies.findIndex(v => (v.type === type && v.name === name));
        const typeExists = f.policies.findIndex(v => (v.type === type));

        if (typeAndPolicyExists !== -1) {
            f.policies.splice(typeAndPolicyExists, 1);
        } else if (typeAndPolicyExists === -1 && typeExists !== -1) {
            f.policies.splice(typeExists, 1);
            f.policies.push({
                type: type,
                name: name,
            });
        } else {
            f.policies.push({
                type: type,
                name: name,
            });
        }

        setFilter(f);
    };

    const handleAddPolicyAll = (policy) => {
        // let c = cmcs.filter(v => v.sensors && v.sensors.length);
        // let formattedList = {};
        // for(let i=0;i<cmcs.length;i++) {
        //     if(cmcs[i].sensors && cmcs[i].sensors.length) {
        //         if(!formattedList[cmcs[i].name]) {
        //             formattedList[cmcs[i].name] = [];
        //         }
        //         formattedList[cmcs[i].name] = formattedList[cmcs[i].name].concat(cmcs[i].sensors);
        //     }
        // }
        PolicyService.assignAllPolicy(policy.type, policy.name).then(res => {
            toast.success('Successfully pushed policy to all CMCs and sensors. It may take some time for the policy to be delivered to the sensors.');
            setFilter({ ...filter });
        }, err => {
            toast.error(err);
        });
    };

    const handleAddPolicyFiltered = (policy) => {
        const c = filteredCMCs.filter(v => v.sensors && v.sensors.length);
        const formattedList = {};
        for (let i = 0; i < c.length; i++) {
            if (!formattedList[c[i].uuid]) {
                formattedList[c[i].uuid] = { sensors: [] };
            }
            formattedList[c[i].uuid].sensors = formattedList[c[i].uuid].sensors.concat(c[i].sensors.map(v => v.uuid));
        }

        if (Object.keys(formattedList).length) {
            PolicyService.assignPolicy(policy.type, policy.name, formattedList).then(res => {
                toast.success('Successfully pushed policy to CMCs and sensors in filter.');
            }, err => {
                toast.error('Unable to push policy ::: ' + err);
            });
        } else {
            toast.warn('Policy not pushed. No sensors in filter.');
        }
    };

    return (
        <React.Fragment>
            <CMCSidebar
                policies={policies}
                filter={filter}
                onSelectPolicy={handlePolicySelect}
                onAddToAll={handleAddPolicyAll}
                onAddToFiltered={handleAddPolicyFiltered}
                onUpdateFilter={handleUpdateFilter}
            />
            {/* <Grid container direction="row" justifyContent="center" alignItems="center"> */}
            <Grid container>
                <Grid item xs={12}>
                    <Box display="flex" mt={-5} justifyContent="flex-end">
                        <Box mt={-0.75}>
                            <Tooltip title="Show/Hide info">
                                <IconButton
                                    aria-label="show or hide CMC info"
                                    onClick={handleToggleIssues}
                                    color={showIssues ? 'secondary' : 'default'}
                                    size="large">
                                    <IssuesIcon />
                                </IconButton>
                            </Tooltip>
                            <IconButton
                                aria-label="expand or collapse cmc rows"
                                onClick={handleExpandAll}
                                size="large">
                                {expandAll ? <Icon>
                                    <img height="24px" src={theme.palette.mode === 'dark' ? '/images/grow-icon-white.png' : '/images/grow-icon-black.png'} />
                                </Icon> : <Icon>
                                    <img height="24px" src={theme.palette.mode === 'dark' ? '/images/shrink-icon-white.png' : '/images/shrink-icon-black.png'} />
                                </Icon>}
                            </IconButton>
                        </Box>
                        {!readonly && <Box ml={2}><Button
                            color="primary"
                            variant="contained"
                            aria-label="add cmc"
                            onClick={handleAddCMC}
                        ><AddIcon /> CMC</Button></Box>}
                        <Menu
                            anchorEl={sortAnchor}
                            open={Boolean(sortAnchor)}
                            onClose={handleCloseSort}
                        >
                            {SORT_TYPES.map((v, i) => {
                                return (
                                    <MenuItem
                                        key={'sort-menu-item-' + i}
                                        className={classes.menuItem}
                                        onClick={() => { handleUpdateSort(v.value) }}
                                    >
                                        <Box display="flex" justifyContent="space-between" width="100%">
                                            {v.name}
                                            {filter.sort.split('+')[0] === v.value ? (filter.sort.split('+')[1] === 'desc' ? <ExpandMore /> : <ExpandLess />) : null}
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    </Box>
                </Grid>
            </Grid>
            <Box mt={2}>
                <Grid container>
                    <Grid item lg={12}>
                        <Box
                            display={(() => {
                                if (filter.host ||
                                    filter.model ||
                                    filter.name ||
                                    filter.q ||
                                    !((filter.status.critical && filter.status.warning && filter.status.normal) ||
                                    (!filter.status.critical && !filter.status.warning && !filter.status.normal)) ||
                                    filter.policies.length) {
                                    return 'flex';
                                }
                                return 'none';
                            })()}
                            mt={1}
                            p={1}
                            // className={classes.allSensorBackground}
                            width="100%"
                        >
                            {filter.policies.map((v, i) => {
                                return (
                                    <Box
                                        key={'filter-policy-' + i}
                                        className={classes.policyFilterListItem}
                                        onClick={() => { handlePolicySelect(v.type, v.name) }}
                                        display="flex"
                                    >
                                        <Box>{(v.name === 'default' ? v.type + ': ' : '') + v.name}</Box>
                                        <Box><CloseIcon className={classes.policyFilterCloseIcon} /></Box>
                                    </Box>
                                );
                            })}
                            {filter.host ? <Box className={classes.policyFilterListItem} display="flex" onClick={() => { handleClearFilter('host') }}>Host: {filter.host}<Box><CloseIcon className={classes.policyFilterCloseIcon} /></Box></Box> : null}
                            {filter.model ? <Box className={classes.policyFilterListItem} display="flex" onClick={() => { handleClearFilter('model') }}>Model: {filter.model}<Box><CloseIcon className={classes.policyFilterCloseIcon} /></Box></Box> : null}
                            {filter.name ? <Box className={classes.policyFilterListItem} display="flex" onClick={() => { handleClearFilter('name') }}>Name: {filter.name}<Box><CloseIcon className={classes.policyFilterCloseIcon} /></Box></Box> : null}
                            {filter.q ? <Box className={classes.policyFilterListItem} display="flex" onClick={() => { handleClearFilter('q') }}>Search: {filter.q}<Box><CloseIcon className={classes.policyFilterCloseIcon} /></Box></Box> : null}
                            {!(filter.status.critical && filter.status.warning && filter.status.normal) ?
                                <Box display="flex">
                                    <Box
                                        className={clsx(classes.policyFilterListItem, classes.policyFilterCritical)}
                                        display={filter.status.critical ? 'flex' : 'none'}
                                        onClick={() => { handleClearStatus('critical') }}
                                    >
                                        <Box>Status: Critical</Box>
                                        <Box><CloseIcon className={classes.policyFilterCloseIcon} /></Box>
                                    </Box>
                                    <Box
                                        className={clsx(classes.policyFilterListItem, classes.policyFilterWarning)}
                                        display={filter.status.warning ? 'flex' : 'none'}
                                        onClick={() => { handleClearStatus('warning') }}
                                    >
                                        <Box>Status: Warning</Box>
                                        <Box><CloseIcon className={classes.policyFilterCloseIcon} /></Box>
                                    </Box>
                                    <Box
                                        className={clsx(classes.policyFilterListItem, classes.policyFilterNormal)}
                                        display={filter.status.normal ? 'flex' : 'none'}
                                        onClick={() => { handleClearStatus('normal') }}
                                    >
                                        <Box>Status: Normal</Box>
                                        <Box><CloseIcon className={classes.policyFilterCloseIcon} /></Box>
                                    </Box>
                                </Box>
                                : null}
                        </Box>
                        <Box py={2} width="100%">
                            <CMCTable cmcs={filteredCMCs} showIssues={showIssues} expandAll={expandAll} policies={policies} allJobs={allJobs} />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display={filteredCMCs.length ? 'none' : 'block'}>
                            No matches
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </React.Fragment>
    );
};

export default CMCs;
